import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { notificationsApi } from '../features/notifications/api.ts';
import { Pagination } from '../components/Pagination.tsx';
import { formatDvDate, formatDvTime } from '../lib/dvDate.ts';
import { dv } from '../i18n/dv.ts';

const PAGE_SIZE = 20;

// Leading tonal icon per notification type (M3 list-item leading element),
// mapped to WapMeet's agenda / yaumiyya / ninmun notification kinds.
function iconFor(type: string): { name: string; cls: string } {
  switch (type) {
    case 'agenda.pdf_failed':
      return { name: 'error', cls: 'bg-[hsl(var(--error-container))] text-[hsl(var(--destructive))]' };
    case 'agenda.passed':
    case 'agenda.finalized':
    case 'yaumiyya.approved':
    case 'ninmun.passed':
      return { name: 'verified', cls: 'bg-[hsl(var(--success-container))] text-[hsl(var(--on-success-container))]' };
    case 'agenda.decided':
      return { name: 'gavel', cls: 'bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]' };
    case 'agenda.reopened':
    case 'yaumiyya.sign_no_account':
      return { name: 'notification_important', cls: 'bg-[hsl(var(--tertiary-container))] text-[hsl(var(--on-tertiary-container))]' };
    case 'agenda.emailed':
      return { name: 'mail', cls: 'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]' };
    case 'agenda.submitted':
    case 'yaumiyya.submitted':
      return { name: 'note_add', cls: 'bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]' };
    default:
      return { name: 'notifications', cls: 'bg-[hsl(var(--surface-container-highest))] text-muted-foreground' };
  }
}

export function Inbox(): JSX.Element {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  // Page-aware notification list. The sidebar/bottom-nav unread badge uses the
  // unpaged top-level ['notifications'] query — keep this one scoped to
  // ['notifications', page] so paging doesn't invalidate the badge's data.
  const { data, isLoading } = useQuery({
    queryKey: ['notifications', page],
    queryFn: () => notificationsApi.list(page, PAGE_SIZE),
  });
  const items = data?.items ?? [];
  const unread = items.filter((i) => !i.isRead).length;

  async function markAll(): Promise<void> {
    await notificationsApi.markAllRead();
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  }

  return (
    <div className="page space-y-5 md:space-y-8">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">{dv.notifications.title}</h1>
          {unread > 0 && (
            <span className="pill pill-active tabular" dir="ltr">{unread}</span>
          )}
        </div>
        {unread > 0 && (
          <button onClick={markAll} className="btn-ghost btn-sm">{dv.notifications.markAllRead}</button>
        )}
      </header>

      {isLoading && (
        <p role="status" aria-live="polite" className="status-label">{dv.common.loading}</p>
      )}

      {!isLoading && items.length === 0 && (
        <div className="card-paper p-10 text-center space-y-3">
          <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-[hsl(var(--primary-container))] text-[hsl(var(--on-primary-container))]">
            <Icon name="notifications" size={22} />
          </div>
          <p className="text-xl text-ink">{dv.notifications.empty}</p>
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-3">
          <ul className="rounded-3xl bg-[hsl(var(--chrome))] overflow-hidden">
            {items.map((n) => {
              const ic = iconFor(n.type);
              return (
                <li key={n.id} className="border-b border-black/[0.05] last:border-b-0">
                  <a
                    href={n.link ?? '#'}
                    onClick={() => void notificationsApi.markRead(n.id).then(() => qc.invalidateQueries({ queryKey: ['notifications'] }))}
                    className="flex items-start gap-4 px-4 sm:px-5 py-4 transition-colors hover:bg-[hsl(var(--surface-high))]"
                  >
                    <span className={`grid size-11 shrink-0 place-items-center rounded-full ${ic.cls}`}>
                      <Icon name={ic.name} size={22} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={n.isRead ? 'text-[0.95rem] font-normal text-foreground' : 'text-[0.95rem] font-semibold text-ink'}>
                        {n.title}
                      </div>
                      <div className="mt-0.5 text-sm text-muted-foreground line-clamp-2">{n.body}</div>
                      <div className="mt-1.5 text-xs text-muted-foreground">
                        {formatDvDate(n.createdAt)} · <span className="tabular" dir="ltr">{formatDvTime(n.createdAt)}</span>
                      </div>
                    </div>
                    {!n.isRead && <span aria-label="unread" className="mt-1.5 size-2.5 shrink-0 rounded-full bg-[hsl(var(--primary))]" />}
                  </a>
                </li>
              );
            })}
          </ul>
          <Pagination
            page={data?.page ?? 1}
            pageSize={data?.pageSize ?? PAGE_SIZE}
            total={data?.total ?? 0}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
